import { inject, Injectable } from '@angular/core';
import { Action } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  TopicHandlingError,
  WebPubSubRequest,
  WebPubSubResponse,
  WebPubSubService
} from '@festo-ax/smartenance-shared-ui';
import { from, Observable, of, switchMap } from 'rxjs';
import { catchError, map, withLatestFrom } from 'rxjs/operators';
import * as sapGlobalsActions from 'app/actions/sap-globals';
import { MachinesService } from 'app/services/machines.service';
import { FALLBACK_LANGUAGE, MINOR_TIMEOUT } from 'app/tokens';
import { MachineBriefData } from 'app/models/api/machines';
import { SapUtils } from 'app/utils/reducers/sap.utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Identity } from '../models/api/identity';
import { UserSettings } from '../models/api/user-settings';
import { TranslateService } from '@ngx-translate/core';
import { ProfileStore } from 'app/state-management/profile/profile.store';

@Injectable()
export class SapGlobalEffects {
  private readonly profileStore = inject(ProfileStore);
  private actions$: Actions = inject(Actions);
  private webPubSubService: WebPubSubService = inject(WebPubSubService);
  private machinesService: MachinesService = inject(MachinesService);
  private translateService: TranslateService = inject(TranslateService);

  private readonly minorTimeout: number = inject(MINOR_TIMEOUT);
  private readonly sapLanguageFallback: string = inject(FALLBACK_LANGUAGE);

  getSystemStates$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getSystemStates),
      map(
        () =>
          ({
            topic: 'getAllSystemStatus',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllSystemStatus'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            {
              timeout: this.minorTimeout
            }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllSystemStatus'>) => sapGlobalsActions.getSystemStatesSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getSystemStatesFail({ error })))
        )
      )
    )
  );

  getUserStates$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getUserStates),
      map(
        () =>
          ({
            topic: 'getAllUserStatus',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllUserStatus'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllUserStatus'>) => sapGlobalsActions.getUserStatesSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getUserStatesFail({ error })))
        )
      )
    )
  );

  getManufacturingWorkcenterList$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getManufacturingWorkcenterList),
      map(
        () =>
          ({
            topic: 'getAllManufacturingWorkCenters',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllManufacturingWorkCenters'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllManufacturingWorkCenters'>) => response.data?.result?.items
            ? sapGlobalsActions.getManufacturingWorkcenterListSuccess({ response })
            : sapGlobalsActions.getManufacturingWorkcenterListFail({ error: this.createErrorTextFromErrorTopicHandler(response.data.topicHandlingError) })
          ),
          catchError(error => of(sapGlobalsActions.getMaintenanceWorkcenterListFail({ error })))
        )
      )
    )
  );

  getMaintenanceWorkcenterList$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getMaintenanceWorkcenterList),
      map(
        () =>
          ({
            topic: 'getAllMaintenanceWorkCenters',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllMaintenanceWorkCenters'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllMaintenanceWorkCenters'>) => response.data?.items
            ? sapGlobalsActions.getMaintenanceWorkcenterListSuccess({ response })
            : sapGlobalsActions.getMaintenanceWorkcenterListFail({ error: this.createErrorTextFromErrorTopicHandler(response.data.topicHandlingError) })
          ),
          catchError(error => of(sapGlobalsActions.getMaintenanceWorkcenterListFail({ error })))
        )
      )
    )
  );

  getFunctionalLocations$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getFunctionalLocations),
      map(
        () =>
          ({
            topic: 'getAllFunctionalLocations',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllFunctionalLocations'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllFunctionalLocations'>) => sapGlobalsActions.getFunctionalLocationsSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getFunctionalLocationsFail({ error })))
        )
      )
    )
  );

  getNotificationTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getNotificationTypes),
      map(
        () =>
          ({
            topic: 'getAllNotificationTypes',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllNotificationTypes'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllNotificationTypes'>) => sapGlobalsActions.getNotificationTypesSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getNotificationTypesFail({ error })))
        )
      )
    )
  );

  getSapMachinesNames$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getSapMachineNames),
      switchMap(() =>
        this.machinesService.getMachinesBriefData(true).pipe(
          map((response: { machines: MachineBriefData[] }) => sapGlobalsActions.getSapMachineNamesSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getSapMachineNamesFail({ error })))
        )
      )
    )
  );

  getAsyncSapMachines$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getAsyncSapMachines),
      map((action) => SapUtils.createRequestForAsyncSapMachines(action.searchText)),
      withLatestFrom(this.profileStore.profile$, this.profileStore.userSettings$),
      switchMap(([request, identity, userSettings]: [WebPubSubRequest<'getAllEquipmentsOverview'>, Identity, UserSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            {
              timeout: this.minorTimeout
            }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllEquipmentsOverview'>) => sapGlobalsActions.getAsyncSapMachinesSuccess({ machines: response?.data?.result?.items || [] })),
          catchError(error => of(sapGlobalsActions.getAsyncSapMachinesFail({ error })))
        )
      )
    )
  );

  getOrderTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getOrderTypes),
      map(
        () =>
          ({
            topic: 'getAllOrderTypes',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllOrderTypes'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllOrderTypes'>) => sapGlobalsActions.getOrderTypesSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getOrderTypesFail({ error })))
        )
      )
    )
  );

  getPlannerGroups$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getPlannerGroups),
      map(
        () =>
          ({
            topic: 'getAllMaintenancePlannerGroups',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllMaintenancePlannerGroups'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllMaintenancePlannerGroups'>) => sapGlobalsActions.getPlannerGroupsSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getPlannerGroupsFail({ error })))
        )
      )
    )
  );

  getCatalogGroups$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getCatalogGroups),
      concatLatestFrom(() => this.profileStore.userSettings$),
      map(([{ filterOptions }, userSettings]) => ({
        filterOptions: { ...filterOptions, language: userSettings?.sapLanguage || this.sapLanguageFallback }
      })),
      map(
        ({ filterOptions }) =>
          ({
            topic: 'getAllCatalogGroups',
            data: { filterOptions }
          } as WebPubSubRequest<'getAllCatalogGroups'>)
      ),
      concatLatestFrom(() => this.profileStore.profile$),
      switchMap(([request, identity]) =>
        from(this.webPubSubService.sendRequest(request, identity, { timeout: this.minorTimeout })).pipe(
          map((response: WebPubSubResponse<'getAllCatalogGroups'>) => sapGlobalsActions.getCatalogGroupsSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getCatalogGroupsFail({ error })))
        )
      )
    )
  );

  getFailureTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getFailureTypes),
      map(
        () =>
          ({
            topic: 'getAllMaintenanceNotificationCodes',
            data: { filterOptions: {} }
          } as WebPubSubRequest<'getAllMaintenanceNotificationCodes'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllMaintenanceNotificationCodes'>) =>
            sapGlobalsActions.getFailureTypesSuccess({ response })
          ),
          catchError(error => of(sapGlobalsActions.getFailureTypesFail({ error })))
        )
      )
    )
  );

  getActivityTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getActivityTypes),
      map(
        () =>
          ({
            topic: 'getAllActivityTypes',
            data: {}
          } as WebPubSubRequest<'getAllActivityTypes'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getAllActivityTypes'>) => sapGlobalsActions.getActivityTypesSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getActivityTypesFail({ error })))
        )
      )
    )
  );

  getLanguageTypes$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sapGlobalsActions.getLanguageTypes),
      map(
        () =>
          ({
            topic: 'getSupportedSapLanguages',
            data: {}
          } as WebPubSubRequest<'getSupportedSapLanguages'>)
      ),
      concatLatestFrom(() => [this.profileStore.profile$, this.profileStore.userSettings$]),
      switchMap(([request, identity, userSettings]) =>
        from(
          this.webPubSubService.sendRequest(
            SapUtils.mergeRequestWithLanguageFromUserSettings(request, userSettings, this.sapLanguageFallback),
            identity,
            { timeout: this.minorTimeout }
          )
        ).pipe(
          map((response: WebPubSubResponse<'getSupportedSapLanguages'>) => sapGlobalsActions.getLanguageTypesSuccess({ response })),
          catchError(error => of(sapGlobalsActions.getLanguageTypesFail({ error })))
        )
      )
    )
  );

  private createErrorTextFromErrorTopicHandler(error: TopicHandlingError | null): string {
    const defaultError: string = this.translateService.instant('MISC.DATA_NOT_LOADED');

    if (!error) {
      return defaultError;
    }
    const errorStatusCode: string = error?.StatusCode ? `${error.StatusCode}: ` : '';
    const errorMessage: string = error?.Message ?? defaultError;

    return errorStatusCode + errorMessage;
  }
}
